import type { SummaryInsight } from '../../../hooks/useAdvancedInsights';

interface Props {
  summary: SummaryInsight;
}

const fmt = (v: number) =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export const InsightsSummaryCards = ({ summary }: Props) => {
  const savingsRate = summary.savingsRate;

  const cards = [
    {
      label: 'Receitas do mês',
      value: fmt(summary.totalReceitas),
      icon: 'ri-arrow-down-circle-line',
      color: 'text-emerald-600',
      bg: 'bg-emerald-50',
      change: summary.receitasChange,
      positive: (summary.receitasChange ?? 0) > 0,
    },
    {
      label: 'Despesas do mês',
      value: fmt(summary.totalDespesas),
      icon: 'ri-arrow-up-circle-line',
      color: 'text-red-500',
      bg: 'bg-red-50',
      change: summary.despesasChange,
      positive: (summary.despesasChange ?? 0) < 0,
    },
    {
      label: 'Saldo do período',
      value: fmt(summary.saldo),
      icon: 'ri-scales-line',
      color: summary.saldo >= 0 ? 'text-emerald-600' : 'text-red-500',
      bg: summary.saldo >= 0 ? 'bg-emerald-50' : 'bg-red-50',
      change: null,
      positive: summary.saldo >= 0,
    },
    {
      label: 'Taxa de poupança',
      value: savingsRate !== null ? `${savingsRate.toFixed(0)}%` : '—',
      icon: 'ri-piggy-bank-line',
      color: savingsRate !== null && savingsRate >= 20 ? 'text-emerald-600' : savingsRate !== null && savingsRate < 10 ? 'text-amber-600' : 'text-gray-600',
      bg: savingsRate !== null && savingsRate >= 20 ? 'bg-emerald-50' : savingsRate !== null && savingsRate < 10 ? 'bg-amber-50' : 'bg-gray-50',
      change: null,
      positive: savingsRate !== null && savingsRate >= 20,
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
      {cards.map((card) => (
        <div key={card.label} className="bg-white rounded-2xl border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[12px] font-500 text-gray-500">{card.label}</p>
            <div className={`w-8 h-8 flex items-center justify-center ${card.bg} rounded-xl`}>
              <i className={`${card.icon} ${card.color} text-[16px]`} />
            </div>
          </div>
          <p className={`text-[22px] font-700 ${card.color} leading-none mb-2`}>{card.value}</p>
          {card.change !== null && (
            <p className={`text-[11px] font-500 flex items-center gap-1 ${card.positive ? 'text-emerald-600' : 'text-red-500'}`}>
              <i className={`${card.positive ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-[13px]`} />
              {Math.abs(Math.round(card.change))}% vs mês anterior
            </p>
          )}
          {card.change === null && card.label === 'Saldo do período' && (
            <p className="text-[11px] text-gray-400">{summary.transactionCount} lançamentos</p>
          )}
          {card.change === null && card.label === 'Taxa de poupança' && savingsRate !== null && (
            <p className={`text-[11px] font-500 ${savingsRate >= 20 ? 'text-emerald-600' : savingsRate < 10 ? 'text-amber-600' : 'text-gray-500'}`}>
              {savingsRate >= 20 ? 'Acima do ideal (20%)' : savingsRate < 10 ? 'Abaixo do ideal (20%)' : 'Próximo ao ideal'}
            </p>
          )}
        </div>
      ))}
    </div>
  );
};
